import { useState } from 'react'
import {
    FloatingNode,
    FloatingPortal,
    offset,
    useClick,
    useDismiss,
    useFloating,
    useFloatingNodeId,
    useInteractions,
    useTransitionStatus,
} from '@floating-ui/react'
import clsx from 'clsx'
import styles from './TreeSelect.module.scss'
import ArrowDown from '../assets/arrow-down.svg'
import { SelectItem } from './TreeSelectItem'
import { TreeSelectOption } from '../types'

interface Props {
    options: TreeSelectOption[]
    value?: string
    disabled?: boolean
    placeholder: string
    onChange: (value: string) => void
}

export const TreeSelect = ({ options, placeholder, disabled, value, onChange }: Props) => {
    const [isOpen, setIsOpen] = useState(false)
    const nodeId = useFloatingNodeId()

    const { x, y, strategy, context, refs } = useFloating({
        nodeId,
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [offset({ mainAxis: 5 })],
        placement: 'bottom-start',
    })

    const dismiss = useDismiss(context)
    const click = useClick(context)

    const { isMounted, status } = useTransitionStatus(context)
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss])

    const getDisplayedValue = (): string => {
        if (!value) {
            return placeholder
        }

        const formattedOptions = options.map((item) => [item, ...(item.options || [])]).flat()
        const selectedOption = formattedOptions.find((item) => item.value === value)

        return selectedOption?.label ?? placeholder
    }

    const handleSelectItem = (value: string) => {
        onChange(value)
        setIsOpen(false)
    }

    return (
        <>
            <button
                ref={refs.setReference}
                className={styles.root}
                disabled={disabled}
                {...getReferenceProps}
                onClick={() => setIsOpen(true)}
            >
                {getDisplayedValue()}
                <span className={clsx(styles.icon, { [styles.iconOpen]: isOpen })}>
                    <ArrowDown />
                </span>
            </button>
            <FloatingNode id={nodeId}>
                {isMounted && (
                    <FloatingPortal>
                        <div
                            ref={refs.setFloating}
                            style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
                            className={styles.select}
                            data-status={status}
                            {...getFloatingProps()}
                        >
                            {options.map((optionConfig, index) => (
                                <SelectItem key={index} onChange={handleSelectItem} {...optionConfig} />
                            ))}
                        </div>
                    </FloatingPortal>
                )}
            </FloatingNode>
        </>
    )
}
